import React, { useEffect, useMemo, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { apiFetch } from "@/lib/api";
import { helper } from "@/lib/helper";
import { cn } from "@/lib/utils";
import { Loader2, StickyNote, User2, Calendar, PencilLine } from "lucide-react";

type NoteRow = {
  id?: number | string;
  created_at?: string;
  note_status?: string;
  note?: string;
  CREATEDBY?: string;
  EDDITEDBY?: string;
};

type PaginationMeta = {
  current_page: number;
  per_page: number;
  total: number;
  last_page: number;
  has_more: boolean;
};

export default function CustomerNotesTab({ customerId }: { customerId: string }) {
  const perPage = 10;

  const [rows, setRows] = useState<NoteRow[]>([]);
  const [meta, setMeta] = useState<PaginationMeta | null>(null);
  const [page, setPage] = useState(1);

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  function normalize(res: any): { rows: NoteRow[]; meta: PaginationMeta | null } {
    const payload = res?.data ?? res;

    // sometimes apiFetch returns array directly
    if (Array.isArray(payload)) return { rows: payload, meta: null };

    const rows =
      (Array.isArray(payload?.data) && payload.data) ||
      (Array.isArray(payload?.data?.data) && payload.data.data) ||
      [];

    const meta = payload?.meta ?? payload?.data?.meta ?? null;

    return { rows, meta };
  }

  function statusPill(status?: string) {
    const s = (status || "").toLowerCase();
    if (s.includes("urgent")) return "bg-red-500/10 text-red-700 border-red-200";
    if (s.includes("pending")) return "bg-amber-500/10 text-amber-700 border-amber-200";
    if (s.includes("closed") || s.includes("done")) return "bg-emerald-500/10 text-emerald-700 border-emerald-200";
    return "bg-primary/10 text-primary border-primary/20";
  }

  async function fetchNotes(p = 1) {
    if (!customerId) return;
    if (loading) return;

    setLoading(true);
    setErr("");

    try {
      const res = await apiFetch<any>(
        `/customer-profile/${customerId}/notes?page=${p}&per_page=${perPage}`
      );

      const out = normalize(res);
      setRows(out.rows);
      setMeta(out.meta);
      setPage(p);
    } catch (e: any) {
      setErr(e?.message || "Failed to load notes");
      setRows([]);
      setMeta(null);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchNotes(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);

  const canPrev = page > 1 && !loading;
  const canNext = Boolean(meta?.has_more) && !loading;

  const totalNotes = useMemo(() => rows.length, [rows]);

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="text-sm font-semibold">Customer Notes</div>

        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => fetchNotes(1)}
            disabled={loading}
            className="gap-2"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Refresh
          </Button>

          <Button size="sm" onClick={() => alert("TODO: Add Note")} className="gap-2">
            <StickyNote className="h-4 w-4" />
            Add Note
          </Button>
        </div>
      </div>

      {/* Error */}
      {err ? (
        <Card>
          <CardContent className="p-4 text-sm text-destructive">{err}</CardContent>
        </Card>
      ) : null}

      {/* Desktop table */}
      <div className="hidden lg:block overflow-x-auto rounded-xl border">
        <table className="w-full text-sm min-w-[900px]">
          <thead className="bg-muted/50">
            <tr className="text-left">
              <th className="p-3">#</th>
              <th className="p-3">Status</th>
              <th className="p-3">Note</th>
              <th className="p-3">Created At</th>
              <th className="p-3">Created By</th>
              <th className="p-3">Edited By</th>
              <th className="p-3">Action</th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan={7} className="p-6 text-muted-foreground">
                  <div className="flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Loading notes...
                  </div>
                </td>
              </tr>
            ) : rows.length ? (
              rows.map((r, idx) => (
                <tr key={String(r.id ?? idx)} className="border-t">
                  <td className="p-3">{idx + 1}</td>

                  <td className="p-3">
                    <span
                      className={cn(
                        "inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border",
                        statusPill(helper.safe(r.note_status))
                      )}
                    >
                      {helper.safe(r.note_status, "Note")}
                    </span>
                  </td>

                  <td className="p-3">
                    <div className="max-w-[520px] whitespace-pre-wrap break-words">
                      {helper.safe(r.note)}
                    </div>
                  </td>

                  <td className="p-3">{helper.safe(r.created_at)}</td>
                  <td className="p-3">{helper.safe(r.CREATEDBY)}</td>
                  <td className="p-3">{helper.safe(r.EDDITEDBY)}</td>

                  <td className="p-3">
                    <Button size="sm" variant="outline" className="gap-2" onClick={() => alert(`TODO: edit note ${r.id}`)}>
                      <PencilLine className="h-4 w-4" />
                      Edit
                    </Button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={7} className="p-6 text-muted-foreground">
                  No notes found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="lg:hidden space-y-3">
        {loading ? (
          <Card className="shadow-card">
            <CardContent className="p-4 text-sm text-muted-foreground flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading notes...
            </CardContent>
          </Card>
        ) : rows.length ? (
          rows.map((r, idx) => (
            <Card key={String(r.id ?? idx)} className="shadow-card rounded-2xl border bg-background">
              <CardContent className="p-4 space-y-3">
                {/* top row */}
                <div className="flex items-start justify-between gap-2">
                  <span
                    className={cn(
                      "inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border",
                      statusPill(helper.safe(r.note_status))
                    )}
                  >
                    {helper.safe(r.note_status, "Note")}
                  </span>

                  <Button
                    size="sm"
                    variant="outline"
                    className="gap-2"
                    onClick={() => alert(`TODO: edit note ${r.id}`)}
                  >
                    <PencilLine className="h-4 w-4" />
                    Edit
                  </Button>
                </div>

                {/* note */}
                <div className="rounded-xl border bg-muted/20 p-3">
                  <div className="text-xs text-muted-foreground mb-1 flex items-center gap-2">
                    <StickyNote className="h-3.5 w-3.5 text-primary" />
                    Note
                  </div>
                  <div className="text-sm font-medium whitespace-pre-wrap break-words">
                    {helper.safe(r.note)}
                  </div>
                </div>

                {/* meta */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <MiniMeta icon={Calendar} label="Created" value={helper.safe(r.created_at)} />
                  <MiniMeta icon={User2} label="By" value={helper.safe(r.CREATEDBY)} />
                   {r.EDDITEDBY !== null &&
                      r.EDDITEDBY !== undefined &&
                      r.EDDITEDBY !== "" && (
                  <MiniMeta icon={User2} label="Edited By" value={helper.safe(r.EDDITEDBY, "—")} />
                   )}
                </div>
              </CardContent>
            </Card>
          ))
        ) : (
          <div className="text-sm text-muted-foreground">No notes found.</div>
        )}
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="text-xs text-muted-foreground">
          {meta ? `Page ${meta.current_page} of ${meta.last_page} • Total ${meta.total}` : `Loaded ${totalNotes}`}
        </div>

        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" disabled={!canPrev} onClick={() => fetchNotes(page - 1)}>
            Prev
          </Button>
          <Button size="sm" variant="outline" disabled={!canNext} onClick={() => fetchNotes(page + 1)}>
            Next
          </Button>
        </div>
      </div>
    </div>
  );
}

function MiniMeta({
  icon: Icon,
  label,
  value,
}: {
  icon: any;
  label: string;
  value: string;
}) {
  return (
    <div className="rounded-xl border bg-background p-3">
      <div className="flex items-center gap-2">
        <div className="h-8 w-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
          <Icon className="h-4 w-4" />
        </div>
        <div className="min-w-0">
          <div className="text-[11px] text-muted-foreground font-medium">{label}</div>
          <div className="text-sm font-semibold truncate">{value}</div>
        </div>
      </div>
    </div>
  );
}